/**
 * @file Global theme settings for Digital Twin.
 */
import { alpha } from '@mui/material';

const GlobalTheme = {
  shape: {
    borderRadius: 8,
  },
  spacing: [0, 8, 16, 24, 32, 40, 48, 56, 64, 72, 80, 88, 96],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiBox: {
      variants: [
        {
          props: { variant: 'content' },
          style: {
            padding: '16px',
            background: '#FFFFFF',
            borderRadius: '12px',
          },
        },
      ],
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: 'Sintony',
          fontWeight: 600,
          fontSize: '0.875rem',
        },
      },
    },
  },
  palette: {
    common: {
      blackStates: {
        active: alpha('#000000', 0.56),
        hover: alpha('#000000', 0.04),
        selected: alpha('#000000', 0.08),
        focus: alpha('#000000', 0.12),
        disabled: alpha('#000000', 0.38),
        disabledBackground: alpha('#000000', 0.12),
      },
      whiteStates: {
        active: alpha('#FFFFFF', 0.56),
        hover: alpha('#FFFFFF', 0.04),
        selected: alpha('#FFFFFF', 0.08),
        focus: alpha('#FFFFFF', 0.12),
        disabled: alpha('#FFFFFF', 0.38),
        disabledBackground: alpha('#FFFFFF', 0.12),
      },
    },
    primary: {
      main: '#002855',
      light: '#26578E',
      dark: '#001124',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#00A9E0',
      dark: '#7b1fa2',
      light: '#ba68c8',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#d32f2f',
      dark: '#c62828',
      light: '#ef5350',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#ed6c02',
      dark: '#ff9800',
      light: '#DBE049',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#1578C9',
      dark: '#004998',
      light: '#2697E8',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#006055',
      light: '#007064',
      dark: '#014439',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#EEEEEE',
      paper: '#FFFFFF',
    },
    text: {
      primary: alpha('#000000', 0.87),
      secondary: alpha('#000000', 0.6),
      disabled: alpha('#000000', 0.38),
    },
    divider: alpha('#000000', 0.12),
    paper: {
      main: '#FFFFFF',
      dark: alpha('#000000', 0.04),
      light: '#FFFFFF',
      contrastText: alpha('#000000', 0.87),
    },
    paperOutline: {
      main: alpha('#000000', 0.87),
    },
    action: {
      active: alpha('#000000', 0.56),
      hover: alpha('#000000', 0.04),
      selected: alpha('#000000', 0.08),
      focus: alpha('#000000', 0.12),
      disabled: alpha('#000000', 0.38),
      disabledBackground: alpha('#000000', 0.12),
    },
  },
  typography: {
    fontFamily: '"Poppins", "Sintony", sans-serif',
    h1: {
      fontFamily: 'Sintony',
      fontSize: '2.625rem',
      fontWeight: 700,
      lineHeight: 1.1,
    },
    h2: {
      fontFamily: 'Sintony',
      fontSize: '2.375rem',
      fontWeight: 700,
      lineHeight: 1.1,
    },
    h3: {
      fontFamily: 'Sintony',
      fontSize: '2.125rem',
      fontWeight: 700,
      lineHeight: 1.05,
    },
    h4: {
      fontFamily: 'Sintony',
      fontSize: '1.625rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h5: {
      fontFamily: 'Sintony',
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.15,
    },
    h6: {
      fontFamily: 'Sintony',
      fontSize: '1.125rem',
      fontWeight: 700,
      lineHeight: 1.6,
    },
    body1: {
      fontFamily: 'Poppins',
      fontSize: '1rem',
      fontWeight: 300,
      lineHeight: 1.2,
    },
    body2: {
      fontFamily: 'Poppins',
      fontSize: '0.875rem',
      fontWeight: 300,
      lineHeight: 1.2,
    },
    subtitle1: {
      fontFamily: 'Poppins',
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.2,
    },
    subtitle2: {
      fontFamily: 'Poppins',
      fontSize: '0.625rem',
      fontWeight: 400,
      lineHeight: 1.2,
    },
  },
};

export default GlobalTheme;
